// FI data freshness: when each input on the page is actually FROM, not when the page last asked.
//
// The page used to show only the time of its last request ("As of 11:39 AM") and re-ask every
// 5 minutes, so it always looked minutes-fresh while its holdings marks came from the morning sync
// and its market series from the prior business day (CIO, 2026-09-14). These are the times that
// decide whether the targets and share counts on screen are current.
//
//   marks   dbo.FixedIncomeHoldings.NAVShare stamps — written 06:15 / 06:45 / 13:45 MT and by
//           "Sync IBKR + Refresh"; drive principal defense, current weights and share counts
//   market  the FRED observation dates behind the credit gate and rate trend (BAA10Y, DGS10)
//   vix     the daily VIX close the z-score ends on (capital preservation)
//   regime  when the daily engine timer (15:15 MT, weekdays) last advanced the persisted state
//   engine_run  the 15:15 run's signal record (dbo.FixedIncomeSignalRuns): flagged when the expected run is
//           missing, held, or saw a stale close. Only present once the backend reports lineage
//           (docs/HANDOFF_FI_SIGNAL_LINEAGE.md WI-7), so older payloads render exactly as before.

export type FreshnessKey = "marks" | "market" | "vix" | "regime" | "engine_run";

export interface FreshnessItem {
  key: FreshnessKey;
  label: string;
  /** ISO timestamp for marks/regime, YYYY-MM-DD for market/vix; null when the payload has none. */
  value: string | null;
  kind: "timestamp" | "date";
  stale: boolean;
  staleNote?: string;
}

type FreshnessMeta = {
  rebalance_plan?: { oldest_price_updated_utc?: string | null } | null;
  holdings?: Array<{ price_updated_utc?: string | null; [k: string]: any }> | null;
  credit_regime?: {
    held?: boolean | null;
    hold_reason?: string | null;
    engine_state_updated_utc?: string | null;
    lineage?: {
      status?: "ok" | "warning" | "problem" | string;
      checks?: Array<{ key: string; level: string; note: string }>;
      run?: { run_at_utc?: string | null } | null;
    } | null;
    fetched?: {
      baa10y_last?: string | null;
      dgs10_last?: string | null;
      baa10y_age_bd?: number | null;
      dgs10_age_bd?: number | null;
    } | null;
  } | null;
  vix_asof_date?: string | null;
  /** FRED live, or the last-good stored copy while FRED is down (backend strategy/fred_store.py, CIO 2026-10-21). */
  fred_source?: FredSource | null;
  [k: string]: any;
};

/** The backend's meta.fred_source: every FRED series this read used, and where it came from. */
export interface FredSource {
  source?: "live" | "stored" | "unavailable" | null;
  /** "FRED unavailable (site maintenance) — using stored series as of 2026-10-20" when not live. */
  message?: string | null;
  kinds?: string[];
}

/** The page-level line shown while FRED is down: stored copy in use (amber) or no usable copy (red). */
export function fredSourceBanner(meta: FreshnessMeta | null | undefined): { level: "warning" | "problem"; text: string } | null {
  const fs = meta?.fred_source;
  if (!fs || (fs.source !== "stored" && fs.source !== "unavailable")) return null;
  const text = fs.message || (fs.source === "stored" ? "FRED unavailable — using stored series" : "FRED unavailable — no stored copy");
  return { level: fs.source === "stored" ? "warning" : "problem", text };
}

/** Marks older than this are flagged: every weekday has a 06:15 and 06:45 MT sync. */
export const MARKS_STALE_HOURS = 24;
/** Business days of lag before a daily series is flagged. FRED normally trails by 1-2; the engine
 *  itself HOLDS the prior regime past 5 (fi_state_machine max_staleness_bdays). */
export const STALE_BUSINESS_DAYS = 3;

/** Weekdays strictly after `fromDate` (YYYY-MM-DD) up to and including today's UTC date. */
export function businessDaysBetween(fromDate: string, nowMs: number): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(fromDate ?? "");
  if (!m) return null;
  const day = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const now = new Date(nowMs);
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  let count = 0;
  for (let t = day + 86_400_000; t <= today; t += 86_400_000) {
    const dow = new Date(t).getUTCDay();
    if (dow !== 0 && dow !== 6) count += 1;
  }
  return count;
}

/** Short chip text per failed check; the full sentence is on the signal card. */
const ENGINE_RUN_NOTES: Record<string, string> = {
  run_recorded: "today's run missing",
  not_hold: "engine held — inputs unavailable",
  obs_date: "run used a stale close",
  fred_source: "run used the stored FRED copy",
};

const latest = (values: Array<string | null | undefined>): string | null =>
  values.filter((v): v is string => typeof v === "string" && v.length > 0).sort().pop() ?? null;

const earliest = (values: Array<string | null | undefined>): string | null =>
  values.filter((v): v is string => typeof v === "string" && v.length > 0).sort()[0] ?? null;

export function fiFreshness(meta: FreshnessMeta | null | undefined, nowMs: number): FreshnessItem[] {
  const items: FreshnessItem[] = [];

  // Holdings marks: the share plan's oldest mark it priced with, else the newest holding stamp.
  const marks = meta?.rebalance_plan?.oldest_price_updated_utc ?? latest((meta?.holdings ?? []).map((h) => h?.price_updated_utc));
  const marksAgeH = marks ? (nowMs - Date.parse(marks)) / 3_600_000 : null;
  const marksStale = marksAgeH != null && Number.isFinite(marksAgeH) && marksAgeH > MARKS_STALE_HOURS;
  items.push({
    key: "marks",
    label: "Marks",
    value: marks,
    kind: "timestamp",
    stale: marksStale,
    staleNote: marksStale ? `over ${MARKS_STALE_HOURS}h old — Sync IBKR` : undefined,
  });

  // Market series: the older of the two observation dates the credit gate and rate trend ran on.
  const cr = meta?.credit_regime ?? null;
  const fetched = cr?.fetched ?? null;
  const marketDate = earliest([fetched?.baa10y_last, fetched?.dgs10_last]);
  const ages = [fetched?.baa10y_age_bd, fetched?.dgs10_age_bd].filter((v): v is number => typeof v === "number");
  const marketAge = ages.length ? Math.max(...ages) : marketDate ? businessDaysBetween(marketDate, nowMs) : null;
  const held = Boolean(cr?.held);
  // While FRED is down the series are the last-good stored copy: same numbers, but say so on the chip.
  const fredStored = meta?.fred_source?.source === "stored";
  const marketStale = held || fredStored || (marketAge != null && marketAge >= STALE_BUSINESS_DAYS);
  items.push({
    key: "market",
    label: "Market Data Through",
    value: marketDate,
    kind: "date",
    stale: marketStale,
    staleNote: held
      ? "engine holding — inputs unavailable"
      : fredStored
        ? "FRED unavailable — stored copy"
        : marketStale
          ? `${marketAge} business days old`
          : undefined,
  });

  const vix = meta?.vix_asof_date ?? null;
  const vixAge = vix ? businessDaysBetween(vix, nowMs) : null;
  const vixStale = vixAge != null && vixAge >= STALE_BUSINESS_DAYS;
  items.push({
    key: "vix",
    label: "VIX Close",
    value: vix,
    kind: "date",
    stale: vixStale,
    staleNote: vixStale ? `${vixAge} business days old` : undefined,
  });

  const regime = cr?.engine_state_updated_utc ?? null;
  const regimeAge = regime ? businessDaysBetween(regime.slice(0, 10), nowMs) : null;
  const regimeStale = regimeAge != null && regimeAge >= STALE_BUSINESS_DAYS;
  items.push({
    key: "regime",
    label: "Regime State Updated",
    value: regime,
    kind: "timestamp",
    stale: regimeStale,
    staleNote: regimeStale ? "daily engine run may not have run" : undefined,
  });

  const lineage = cr?.lineage ?? null;
  if (lineage) {
    const problem = (lineage.checks ?? []).find((c) => c.level === "problem");
    items.push({
      key: "engine_run",
      label: "Engine Run Recorded",
      value: lineage.run?.run_at_utc ?? null,
      kind: "timestamp",
      stale: lineage.status === "problem",
      staleNote: problem ? ENGINE_RUN_NOTES[problem.key] ?? "engine run problem" : undefined,
    });
  }

  return items;
}
